import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Wrench, ArrowLeft, ArrowRight, ExternalLink, Phone, Layers, CheckCircle2, Bookmark, Download } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ProviderList } from '../components/ProviderList';

const YesNo = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-0">
    <span className="text-sm font-medium">{label}</span>
    <div className="flex gap-2">
      {[[true, 'Yes'], [false, 'No']].map(([v, l]) => (
        <Button key={l} size="sm" variant={value === v ? 'default' : 'outline'} className={value === v ? 'bg-primary text-primary-foreground' : ''} onClick={() => onChange(v)} data-testid={`${testId}-${l.toLowerCase()}`}>{l}</Button>
      ))}
    </div>
  </div>
);

export default function Equipment() {
  const [devices, setDevices] = useState([]);
  const [step, setStep] = useState(0);
  const [device, setDevice] = useState(null);
  const [ans, setAns] = useState({ on_odsp: false, is_child: false, has_private_insurance: false, is_senior: false });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.post('/equipment/plan/pdf', { device_id: device.id, ...ans }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `funding-plan-${device.id}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded — bring it to your OT or caseworker');
    } catch { toast.error('Could not create the PDF'); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    api.get('/equipment').then(({ data }) => setDevices(data.devices));
    api.get('/profile').then(({ data }) => setAns((a) => ({ ...a, on_odsp: !!data.on_odsp, is_child: data.age != null && data.age < 18, is_senior: data.age != null && data.age >= 65 }))).catch(() => {});
  }, []);

  const build = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/equipment/plan', { device_id: device.id, ...ans });
      setPlan(data); setStep(2);
    } catch { toast.error('Could not build your funding plan'); }
    finally { setLoading(false); }
  };

  const track = async (programId, name) => {
    try { await api.post('/applications', { program_id: programId, status: 'researching', next_action: `Funding for ${device.name}` }); toast.success(`Added "${name}" to My Applications`); }
    catch { toast.error('Could not add to tracker'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4"><Wrench className="w-3.5 h-3.5" /> Equipment Funding Finder</div>
      <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>Stack funding for the device you need</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">Ontario pays first (ADP), then ODSP or insurance, then charities cover what's left. Tell us the device and we'll lay out the order to apply in.</p>

      <div className="flex items-center gap-2 mt-6 text-xs font-medium" data-testid="equipment-steps">
        {['Device', 'Your situation', 'Funding plan'].map((s, i) => <React.Fragment key={s}><span className={`px-2.5 py-1 rounded-full ${step === i ? 'bg-primary text-primary-foreground' : step > i ? 'bg-[#E6F4EA] text-[#0F5132]' : 'bg-secondary text-muted-foreground'}`}>{i + 1}. {s}</span>{i < 2 && <span className="text-muted-foreground">→</span>}</React.Fragment>)}
      </div>

      {step === 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="device-grid">
          {devices.map((d) => (
            <button key={d.id} onClick={() => { setDevice(d); setStep(1); }} data-testid={`device-${d.id}`} className={`text-left p-4 rounded-xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md ${device?.id === d.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'}`}>
              <div className="font-semibold" style={{fontFamily: 'Lexend'}}>{d.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{d.adp_fixed ? `ADP grant ${d.adp_fixed}` : d.adp_share ? `ADP pays ${d.adp_share}%` : 'Not covered by ADP'}</div>
            </button>
          ))}
        </div>
      )}

      {step === 1 && device && (
        <Card className="p-5 sm:p-6 mt-6 bg-card max-w-2xl">
          <div className="text-sm text-muted-foreground mb-1">Device</div>
          <div className="font-semibold text-lg mb-4" style={{fontFamily: 'Lexend'}}>{device.name}</div>
          <YesNo label="Do you (or the person) receive ODSP?" value={ans.on_odsp} onChange={(v) => setAns({ ...ans, on_odsp: v })} testId="eq-odsp" />
          <YesNo label="Is the device for a child under 18?" value={ans.is_child} onChange={(v) => setAns({ ...ans, is_child: v })} testId="eq-child" />
          <YesNo label="Is there private or employer health insurance?" value={ans.has_private_insurance} onChange={(v) => setAns({ ...ans, has_private_insurance: v })} testId="eq-insurance" />
          <YesNo label="Is the person 65 or older?" value={ans.is_senior} onChange={(v) => setAns({ ...ans, is_senior: v })} testId="eq-senior" />
          <div className="flex gap-2 mt-5">
            <Button variant="outline" onClick={() => setStep(0)} data-testid="eq-back"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
            <Button onClick={build} disabled={loading} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="eq-build-plan">{loading ? 'Building…' : 'Build my funding plan'} <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </Card>
      )}

      {step === 2 && plan && (
        <div className="mt-6" data-testid="funding-plan">
          <div className="flex items-center gap-2 mb-4 flex-wrap"><Layers className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold" style={{fontFamily: 'Lexend'}}>Your funding stack for {plan.device.name}</h2><Badge variant="outline">{plan.stack.length} layers</Badge>
            <Button size="sm" onClick={downloadPdf} disabled={downloading} className="ml-auto bg-[#C26227] hover:bg-[#C26227]/90 text-white" data-testid="plan-download-pdf"><Download className="w-4 h-4 mr-1.5" /> {downloading ? 'Preparing…' : 'Download printable PDF'}</Button>
          </div>
          <div className="space-y-3">
            {plan.stack.map((s) => (
              <Card key={s.layer} className="p-5 bg-card" data-testid={`plan-layer-${s.layer}`}>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">{s.layer}</div>
                  <div className="flex-1">
                    <div className="font-semibold" style={{fontFamily: 'Lexend'}}>{s.source}</div>
                    <div className="text-sm text-primary font-medium mt-0.5">Covers: {s.coverage}</div>
                    <p className="text-sm text-muted-foreground mt-1.5">{s.detail}</p>
                    <div className="mt-2 p-3 rounded-lg bg-secondary text-sm"><strong>Do this:</strong> {s.action}</div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {s.url && <Button asChild size="sm" variant="outline"><a href={s.url} target="_blank" rel="noopener noreferrer">Official info <ExternalLink className="w-3 h-3 ml-1" /></a></Button>}
                      {s.program_id && <Button size="sm" variant="outline" onClick={() => track(s.program_id, s.source)} data-testid={`plan-track-${s.program_id}`}><Bookmark className="w-3.5 h-3.5 mr-1" /> Track</Button>}
                    </div>
                    {s.charities && (
                      <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="charity-list">
                        {s.charities.map((c) => (
                          <div key={c.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`charity-${c.id}`}>
                            <div className="font-medium leading-snug">{c.name}</div>
                            <div className="text-xs text-primary mt-1">{c.amount}</div>
                            <p className="text-xs text-muted-foreground mt-1">{c.note}</p>
                            <div className="text-xs mt-1.5 flex gap-3"><a href={c.url} target="_blank" rel="noopener noreferrer" className="text-primary underline flex items-center gap-1">Website <ExternalLink className="w-3 h-3" /></a><span className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" /> {c.phone}</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                    {s.layer === 1 && s.program_id === 'adp_wheelchair' && (
                      <Accordion type="single" collapsible className="mt-3"><AccordionItem value="prov" className="border-t border-border pt-1"><AccordionTrigger className="text-sm font-medium" data-testid="plan-providers-toggle">Find an ADP authorizer or vendor in Toronto</AccordionTrigger><AccordionContent><ProviderList programId="adp_wheelchair" /></AccordionContent></AccordionItem></Accordion>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <Card className="p-5 mt-5 bg-card">
            <div className="font-semibold mb-2 flex items-center gap-2" style={{fontFamily: 'Lexend'}}><CheckCircle2 className="w-4 h-4 text-primary" /> Documents to gather once</div>
            <ul className="grid sm:grid-cols-2 gap-1.5 text-sm">{plan.documents.map((d) => <li key={d} className="flex items-start gap-1.5 text-foreground/80"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />{d}</li>)}</ul>
          </Card>
          <Button variant="outline" className="mt-5" onClick={() => { setStep(0); setPlan(null); }} data-testid="eq-start-over"><ArrowLeft className="w-4 h-4 mr-2" /> Choose a different device</Button>
        </div>
      )}
    </div>
  );
}
